import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getComponents;

export type DbComponentsQueryData = ApiResponse<typeof apiFn>;

export type DbComponentsQueryArgs<SELECT> = QueryOptions<
  DbComponentsQueryData,
  SELECT
> & {
  key: string;
  getRange: () => TimeRange;
};

const DBO_COMPONENTS_QUERY_KEY = 'dbo-components';

export const getDbComponentsQueryKey = (key?: string) =>
  key
    ? ([DBO_COMPONENTS_QUERY_KEY, key] as const)
    : ([DBO_COMPONENTS_QUERY_KEY] as const);

const getDbComponentsQueryOptions = ({
  key,
  getRange,
  enabled,
  refetchInterval,
}: Omit<DbComponentsQueryArgs<unknown>, 'select'>) =>
  queryOptions({
    queryKey: getDbComponentsQueryKey(key),
    queryFn: async () => {
      const { from, to } = getRange();
      return apiFn({ startTime: from, endTime: to }).then(({ data }) => data);
    },
    enabled,
    refetchInterval,
  });

export const useDbComponentsQuery = <SELECT = DbComponentsQueryData>({
  key,
  getRange,
  enabled,
  refetchInterval,
  select,
}: DbComponentsQueryArgs<SELECT>) => {
  return useQuery({
    ...getDbComponentsQueryOptions({ key, getRange, enabled, refetchInterval }),
    select,
  });
};
